import { User } from '@sentry/react';
import classNames from 'classnames';
import UISpinner from 'Components/UIComponents/UISpinner';
import { SelectableItem } from 'Interfaces/Common';
import { TeamMember } from 'Interfaces/Team';
import React, { useMemo } from 'react';
import TeamMemberItemMobile from './TeamMemberItemMobile';
import IconRemove from 'Assets/images/icons/remove-icon.svg';
import IconPause from 'Assets/images/icons/pause.svg';
import IconPlay from 'Assets/images/icons/play.svg';
import { ReactSVG } from 'react-svg';
import { UserRoles } from 'Interfaces/User';
import { LtdTypes } from 'Interfaces/Billing';

interface TeamMemberListMobileViewProps {
  isLoading: boolean;
  teamMembers: SelectableItem<TeamMember>[];
  isEditEnabled: boolean;
  isChangingRoleEnabled: boolean;
  toggleItemSelection?: (teamMemberId: string) => void;
  user: User;
  isOwnerOrAdmin?: boolean;
  ltdType: LtdTypes;
  openDeleteModal: () => void;
  openFreezeModal: () => void;
  openUnfreezeModal: () => void;
}

export const TeamMemberListMobileView = ({
  isLoading,
  teamMembers,
  isEditEnabled,
  isChangingRoleEnabled,
  toggleItemSelection,
  user,
  isOwnerOrAdmin,
  ltdType,
  openDeleteModal,
  openFreezeModal,
  openUnfreezeModal,
}: TeamMemberListMobileViewProps) => {
  const selectedTeamMembers = teamMembers.filter(teamMember => teamMember.isSelected);

  const [isFrozenSelected, isActiveSelected] = useMemo(
    () => [
      selectedTeamMembers.some(teamMember => teamMember.frozenUntil),
      selectedTeamMembers.some(teamMember => !teamMember.frozenUntil),
    ],
    [selectedTeamMembers],
  );

  return (
    <div className="table documents__table">
      <div
        className={classNames(
          'table__tableControls tableControls__controlsGroup team__controlsGroup',
        )}
      >
        {isOwnerOrAdmin && (
          <>
            {ltdType === LtdTypes.NONE && (
              <>
                <button
                  onClick={openFreezeModal}
                  className="tableControls__control tableControls__control--blue"
                  disabled={selectedTeamMembers.length === 0 || isFrozenSelected}
                >
                  <ReactSVG src={IconPause} className="tableControls__control-icon" />
                  Freeze
                </button>
                <button
                  onClick={openUnfreezeModal}
                  className="tableControls__control tableControls__control--blue"
                  disabled={selectedTeamMembers.length === 0 || isActiveSelected}
                >
                  <ReactSVG src={IconPlay} className="tableControls__control-icon" />
                  Unfreeze
                </button>
              </>
            )}
            <button
              onClick={openDeleteModal}
              className="tableControls__control tableControls__control--red"
            >
              <ReactSVG src={IconRemove} className="tableControls__control-icon" />
              Delete
            </button>
          </>
        )}
      </div>
      <div className="table__container">
        <div className="table__innerContainer">
          <div className="table__row table__header mobile">
            <div className="table__column team__column-member">MEMBERS</div>
          </div>
          {isLoading ? (
            <div className="documents__spinner">
              <UISpinner width={50} height={50} className="spinner--main__wrapper" />
            </div>
          ) : (
            teamMembers.map(teamMember => {
              return (
                <TeamMemberItemMobile
                  key={teamMember.id}
                  teamMember={teamMember}
                  ltdType={ltdType}
                  isChangingRoleEnabled={isChangingRoleEnabled}
                  isEditEnabled={isEditEnabled}
                  toggleSelect={() =>
                    toggleItemSelection && toggleItemSelection(teamMember.id)
                  }
                  isSelected={teamMember.isSelected}
                  isSelectable={
                    teamMember.id !== user.id &&
                    teamMember.role !== UserRoles.OWNER &&
                    (user.role === UserRoles.OWNER ||
                      (teamMember.role === UserRoles.USER && isOwnerOrAdmin))
                  }
                  className={classNames({
                    'table__dataRow--frozen': teamMember.frozenUntil,
                  })}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
